import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import StickerText, { stickerTextStyle } from './StickerText';
import { CloseIcon, PlusIcon, TrashIcon } from './Icons';
import { fetchAllPosts, updateUserOnServer, API_URL } from '../api';
import { uploadMediaFile } from '../utils/mediaUtils';
import { PlaylistItem } from '../types';

interface PlaylistModalProps {
    isOpen: boolean;
    onClose: () => void;
    profileUser: string;
    isOwner: boolean;
}

export const PlaylistModal: React.FC<PlaylistModalProps> = ({
    isOpen,
    onClose,
    profileUser,
    isOwner
}) => {
    const {
        currentUser,
        usersData,
        updateUserData,
        playAudioPost,
        pauseAudioPost,
        audioIsPlaying,
        isDemo
        } = useAuth();

    const [isEditing, setIsEditing] = useState(false);
    const [selectedExternalTrack, setSelectedExternalTrack] = useState<PlaylistItem | null>(null);
    const [externalLink, setExternalLink] = useState('');
    const [externalTitle, setExternalTitle] = useState('');
    const [externalError, setExternalError] = useState('');
    const [activeInputService, setActiveInputService] = useState<'spotify' | 'apple' | 'youtube' | null>(null);

    // Pre-resolved metadata cache to prevent duplicate fetches & race conditions
    const [resolvedExternalData, setResolvedExternalData] = useState<{
        previewUrl?: string;
        coverArt?: string;
        title?: string;
    } | null>(null);

    const [resolvedEditData, setResolvedEditData] = useState<{
        previewUrl?: string;
        coverArt?: string;
        title?: string;
    } | null>(null);
    const fetchSpotifyOembed = async (url: string) => {
        try {
            const oembedUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(url)}`;
            const res = await fetch(oembedUrl);
            if (res.ok) {
                return await res.json();
            }
        } catch (err) {
            console.error('Failed to fetch Spotify oembed on client:', err);
        }
        return null;
    };

    // Auto-resolve metadata on paste/change of externalLink
    useEffect(() => {
        const link = externalLink.trim();
        if (!link) {
            setResolvedExternalData(null);
            return;
        }

        const isSpotify = link.includes('spotify.com');
        const isApple = link.includes('apple.com') || link.includes('music.apple.com');
        const isYoutube = link.includes('youtube.com') || link.includes('youtu.be');

        if ((activeInputService === 'spotify' && isSpotify) ||
            (activeInputService === 'apple' && isApple) ||
            (activeInputService === 'youtube' && isYoutube)) {
            
            const controller = new AbortController();
            const fetchPreview = async () => {
                setExternalError('');
                setExternalTitle('Fetching track info...');
                setResolvedExternalData(null);
                try {
                    let endpoint = '';
                    if (isSpotify) {
                        const oembed = await fetchSpotifyOembed(link);
                        const oembedTitle1 = oembed?.title || '';
                        const byIdx1 = oembedTitle1.lastIndexOf(' by ');
                        const trackName1 = byIdx1 > 0 ? oembedTitle1.substring(0, byIdx1).trim() : oembedTitle1.trim();
                        const artistName1 = byIdx1 > 0 ? oembedTitle1.substring(byIdx1 + 4).trim() : '';
                        const titleParam = trackName1 ? `&title=${encodeURIComponent(trackName1)}` : '';
                        const artistParam1 = artistName1 ? `&artist=${encodeURIComponent(artistName1)}` : '';
                        const coverParam = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                        endpoint = `${API_URL}spotify-preview?url=${encodeURIComponent(link)}${titleParam}${artistParam1}${coverParam}`;
                    } else if (isApple) {
                        endpoint = `${API_URL}apple-music-preview?url=${encodeURIComponent(link)}`;
                    } else if (isYoutube) {
                        endpoint = `${API_URL}youtube-music-preview?url=${encodeURIComponent(link)}`;
                    }

                    const response = await fetch(endpoint, { signal: controller.signal });
                    if (response.ok) {
                        const data = await response.json();
                        let fetchedTitle = '';
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                        if (fetchedTitle) {
                            setExternalTitle(fetchedTitle);
                        } else {
                            setExternalTitle('');
                        }
                        setResolvedExternalData({
                            previewUrl: data.previewUrl,
                            coverArt: data.coverImg,
                            title: fetchedTitle
                        });
                    } else {
                        setExternalTitle('');
                        setResolvedExternalData(null);
                    }
                } catch (err: any) {
                    if (err.name !== 'AbortError') {
                        console.error('Failed to pre-fetch details:', err);
                        setExternalTitle('');
                        setResolvedExternalData(null);
                    }
                }
            };

            const delayDebounce = setTimeout(() => {
                fetchPreview();
            }, 500);

            return () => {
                clearTimeout(delayDebounce);
                controller.abort();
            };
        }
    }, [externalLink, activeInputService]);

    // Auto-resolve metadata on change of editLink
    const [editTitle, setEditTitle] = useState('');
    const [editLink, setEditLink] = useState('');

    useEffect(() => {
        const link = editLink.trim();
        if (!link) {
            setResolvedEditData(null);
            return;
        }

        const isSpotify = link.includes('spotify.com');
        const isApple = link.includes('apple.com') || link.includes('music.apple.com');
        const isYoutube = link.includes('youtube.com') || link.includes('youtu.be');

        if (isSpotify || isApple || isYoutube) {
            const controller = new AbortController();
            const fetchPreview = async () => {
                setResolvedEditData(null);
                try {
                    let endpoint = '';
                    if (isSpotify) {
                        const oembed = await fetchSpotifyOembed(link);
                        const oembedTitle2 = oembed?.title || '';
                        const byIdx2 = oembedTitle2.lastIndexOf(' by ');
                        const trackName2 = byIdx2 > 0 ? oembedTitle2.substring(0, byIdx2).trim() : oembedTitle2.trim();
                        const artistName2 = byIdx2 > 0 ? oembedTitle2.substring(byIdx2 + 4).trim() : '';
                        const titleParam2 = trackName2 ? `&title=${encodeURIComponent(trackName2)}` : '';
                        const artistParam2 = artistName2 ? `&artist=${encodeURIComponent(artistName2)}` : '';
                        const coverParam2 = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                        endpoint = `${API_URL}spotify-preview?url=${encodeURIComponent(link)}${titleParam2}${artistParam2}${coverParam2}`;
                    } else if (isApple) {
                        endpoint = `${API_URL}apple-music-preview?url=${encodeURIComponent(link)}`;
                    } else if (isYoutube) {
                        endpoint = `${API_URL}youtube-music-preview?url=${encodeURIComponent(link)}`;
                    }

                    const response = await fetch(endpoint, { signal: controller.signal });
                    if (response.ok) {
                        const data = await response.json();
                        let fetchedTitle = '';
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                        if (fetchedTitle) {
                            setEditTitle(fetchedTitle);
                        }
                        setResolvedEditData({
                            previewUrl: data.previewUrl,
                            coverArt: data.coverImg,
                            title: fetchedTitle
                        });
                    } else {
                        setResolvedEditData(null);
                    }
                } catch (err: any) {
                    if (err.name !== 'AbortError') {
                        console.error('Failed to pre-fetch edit details:', err);
                        setResolvedEditData(null);
                    }
                }
            };

            const delayDebounce = setTimeout(() => {
                fetchPreview();
            }, 500);

            return () => {
                clearTimeout(delayDebounce);
                controller.abort();
            };
        }
    }, [editLink]);

    const handleAddStreamingTrack = async (e: React.FormEvent) => {
        e.preventDefault();
        setExternalError('');

        if (!externalLink.trim()) {
            setExternalError('Please enter a link.');
            return;
        }

        const isSpotify = externalLink.includes('spotify.com');
        const isApple = externalLink.includes('apple.com') || externalLink.includes('music.apple.com');
        const isYoutube = externalLink.includes('youtube.com') || externalLink.includes('youtu.be');

        if (activeInputService === 'spotify' && !isSpotify) {
            setExternalError('Please enter a valid Spotify link.');
            return;
        }
        if (activeInputService === 'apple' && !isApple) {
            setExternalError('Please enter a valid Apple Music link.');
            return;
        }
        if (activeInputService === 'youtube' && !isYoutube) {
            setExternalError('Please enter a valid YouTube/YouTube Music link.');
            return;
        }

        let finalUrl = externalLink.trim();
        let originalUrl = externalLink.trim();
        let coverArt = '';
        let fetchedTitle = '';

        if (resolvedExternalData && resolvedExternalData.previewUrl) {
            console.log('[PlaylistModal] Leveraging pre-resolved external track metadata:', resolvedExternalData);
            finalUrl = resolvedExternalData.previewUrl;
            coverArt = resolvedExternalData.coverArt || '';
            fetchedTitle = resolvedExternalData.title || '';
        } else {
            console.log('[PlaylistModal] Cached metadata not available or incomplete. Performing fallback resolution fetch...');
            if (isSpotify) {
                try {
                    const oembed = await fetchSpotifyOembed(originalUrl);
                    const oembedTitleA = oembed?.title || '';
                    const byIdxA = oembedTitleA.lastIndexOf(' by ');
                    const trackNameA = byIdxA > 0 ? oembedTitleA.substring(0, byIdxA).trim() : oembedTitleA.trim();
                    const artistNameA = byIdxA > 0 ? oembedTitleA.substring(byIdxA + 4).trim() : '';
                    const titleParamA = trackNameA ? `&title=${encodeURIComponent(trackNameA)}` : '';
                    const artistParamA = artistNameA ? `&artist=${encodeURIComponent(artistNameA)}` : '';
                    const coverParamA = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                    const response = await fetch(`${API_URL}spotify-preview?url=${encodeURIComponent(originalUrl)}${titleParamA}${artistParamA}${coverParamA}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) {
                            finalUrl = data.previewUrl;
                        }
                        if (data.coverImg) {
                            coverArt = data.coverImg;
                        }
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Spotify preview:', err);
                }
            } else if (isApple) {
                try {
                    const response = await fetch(`${API_URL}apple-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) {
                            finalUrl = data.previewUrl;
                        }
                        if (data.coverImg) {
                            coverArt = data.coverImg;
                        }
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Apple Music preview:', err);
                }
            } else if (isYoutube) {
                try {
                    const response = await fetch(`${API_URL}youtube-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) {
                            finalUrl = data.previewUrl;
                        }
                        if (data.coverImg) {
                            coverArt = data.coverImg;
                        }
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch YouTube preview:', err);
                }
            }
        }

        const defaultTitle = fetchedTitle || (isSpotify ? 'Spotify Track' : isApple ? 'Apple Music Track' : 'YouTube Track');
        const finalTitle = externalTitle.trim() || defaultTitle;

        const newTrack: PlaylistItem = {
            id: `ext-${Date.now()}`,
            title: finalTitle,
            url: finalUrl,
            originalUrl: originalUrl,
            coverArt: coverArt
        };

        if (playlist.some(t => t.url === newTrack.url || (t.originalUrl && t.originalUrl === newTrack.originalUrl))) {
            setExternalError('Track already in playlist.');
            return;
        }

        const updatedList = [...playlist, newTrack];
        handleSavePlaylist(updatedList);

        // Clear fields
        setExternalLink('');
        setExternalTitle('');
        setExternalError('');
        setActiveInputService(null);
    };

    const getSpotifyEmbedUrl = (url: string) => {
        const match = url.match(/(?:https?:\/\/)?(?:open\.)?spotify\.com\/(track|playlist|album|artist|show|episode)\/([a-zA-Z0-9]+)/);
        if (match && match[1] && match[2]) {
            return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator&autoplay=1`;
        }
        return null;
    };

    const getAppleMusicEmbedUrl = (url: string) => {
        if (url.includes('music.apple.com')) {
            const embed = url.replace('music.apple.com', 'embed.music.apple.com');
            return embed + (embed.includes('?') ? '&autoplay=true' : '?autoplay=true');
        }
        return null;
    };

    const handleNextTrack = () => {
        if (playlist.length === 0 || !selectedExternalTrack) return;
        const curIndex = playlist.findIndex(t => t.id === selectedExternalTrack.id);
        if (curIndex === -1) return;
        const nextIndex = (curIndex + 1) % playlist.length;
        handlePlayTrack(playlist[nextIndex]);
    };

    const handlePrevTrack = () => {
        if (playlist.length === 0 || !selectedExternalTrack) return;
        const curIndex = playlist.findIndex(t => t.id === selectedExternalTrack.id);
        if (curIndex === -1) return;
        const prevIndex = (curIndex - 1 + playlist.length) % playlist.length;
        handlePlayTrack(playlist[prevIndex]);
    };

    const handlePlayTrack = (track: PlaylistItem) => {
        const isSpotify = track.url.includes('spotify.com') || (track.originalUrl && track.originalUrl.includes('spotify.com'));
        const isAppleMusic = track.url.includes('apple.com') || track.url.includes('music.apple.com') || (track.originalUrl && (track.originalUrl.includes('apple.com') || track.originalUrl.includes('music.apple.com')));

        // A direct preview is an MP3 preview link (typically on p.scdn.co, audio-ssl.itunes.apple.com, or any endpoint returning raw MP3)
        // We consider track.url to be a direct preview if it does NOT contain spotify.com/apple.com music page URL hosts.
        const hasDirectPreview = track.url && !track.url.includes('spotify.com') && !track.url.includes('apple.com') && !track.url.includes('music.apple.com');

        if ((isSpotify || isAppleMusic) && !hasDirectPreview) {
            if (pauseAudioPost) {
                pauseAudioPost();
            }
            setSelectedExternalTrack(track);
            return;
        }

        // If it has a direct preview URL, play through the built-in HTML5 player exclusively!
        // We set selectedExternalTrack to null so that we do not render the Spotify/Apple iframe embed box.
        setSelectedExternalTrack(null);

        const tracks = playlist.map((t: any) => ({
            id: t.postId || `playlist-${profileUser}-${t.id}`,
            author: profileUser,
            type: 'audio',
            content: {
                text: t.title,
                media: [{ url: t.url, type: 'audio', originalUrl: t.originalUrl, coverArt: t.coverArt }]
            },
            timestamp: new Date().toISOString()
        }));

        const targetPost = tracks.find(p => p.id === (track.postId || `playlist-${profileUser}-${track.id}`));
        if (targetPost) {
            playAudioPost(targetPost, tracks);
        }
    };
    const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
    const [editingTrackId, setEditingTrackId] = useState<string | null>(null);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;

        const updatedList = [...playlist];
        const [draggedItem] = updatedList.splice(draggedIndex, 1);
        updatedList.splice(targetIndex, 0, draggedItem);

        setPlaylist(updatedList);
        await handleSavePlaylist(updatedList);
        setDraggedIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleStartEdit = (track: PlaylistItem) => {
        setEditingTrackId(track.id);
        setEditTitle(track.title);
        setEditLink(track.originalUrl || track.url);
    };

    const handleSaveEdit = async (index: number) => {
        const track = playlist[index];
        if (!editTitle.trim() || !editLink.trim()) return;

        setIsSavingEdit(true);
        let finalUrl = editLink.trim();
        let originalUrl = editLink.trim();
        let coverArt = track.coverArt || '';

        const isSpotify = editLink.includes('spotify.com');
        const isApple = editLink.includes('apple.com') || editLink.includes('music.apple.com');
        const isYoutube = editLink.includes('youtube.com') || editLink.includes('youtu.be');

        if (resolvedEditData && resolvedEditData.previewUrl) {
            console.log('[PlaylistModal] Leveraging pre-resolved edit track metadata:', resolvedEditData);
            finalUrl = resolvedEditData.previewUrl;
            coverArt = resolvedEditData.coverArt || coverArt;
        } else {
            console.log('[PlaylistModal] Cached edit metadata not available or incomplete. Performing fallback resolution fetch...');
            if (isSpotify && (editLink !== track.originalUrl || !track.url.includes('p.scdn.co'))) {
                try {
                    const oembed = await fetchSpotifyOembed(originalUrl);
                    const oembedTitleB = oembed?.title || '';
                    const byIdxB = oembedTitleB.lastIndexOf(' by ');
                    const trackNameB = byIdxB > 0 ? oembedTitleB.substring(0, byIdxB).trim() : oembedTitleB.trim();
                    const artistNameB = byIdxB > 0 ? oembedTitleB.substring(byIdxB + 4).trim() : '';
                    const titleParamB = trackNameB ? `&title=${encodeURIComponent(trackNameB)}` : '';
                    const artistParamB = artistNameB ? `&artist=${encodeURIComponent(artistNameB)}` : '';
                    const coverParamB = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                    const response = await fetch(`${API_URL}spotify-preview?url=${encodeURIComponent(originalUrl)}${titleParamB}${artistParamB}${coverParamB}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Spotify preview:', err);
                }
            } else if (isApple && (editLink !== track.originalUrl || !track.url.includes('mp3-preview'))) {
                try {
                    const response = await fetch(`${API_URL}apple-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Apple Music preview:', err);
                }
            } else if (isYoutube && (editLink !== track.originalUrl || (!track.url.includes('googlevideo.com') && !track.url.includes('youtube.com')))) {
                try {
                    const response = await fetch(`${API_URL}youtube-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch YouTube preview:', err);
                }
            }
        }

        const updatedList = [...playlist];
        updatedList[index] = {
            ...track,
            title: editTitle.trim(),
            url: finalUrl,
            originalUrl: (isSpotify || isApple || isYoutube) ? originalUrl : undefined,
            coverArt: coverArt
        };

        await handleSavePlaylist(updatedList);
        setEditingTrackId(null);
        setIsSavingEdit(false);
    };
    const [myAudioPosts, setMyAudioPosts] = useState<any[]>([]);
    const [isLoadingPosts, setIsLoadingPosts] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const userData = usersData[profileUser];
    const currentUserData = usersData[currentUser];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        const rgb = hexToRgb(color);
        if (!rgb) return 'rgba(0, 0, 0, 0.5)';
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    // Initialize playlist from userData
    useEffect(() => {
        if (userData?.playlist) {
            setPlaylist(userData.playlist);
        } else {
            setPlaylist([]);
        }
    }, [userData, isOpen]);

    // Fetch user's audio posts if they are the owner and editing
    useEffect(() => {
        if (isOwner && isOpen) {
            setIsLoadingPosts(true);
            fetchAllPosts(profileUser)
                .then((posts) => {
                    const audio = posts.filter((p: any) => {
                        const media = p.content?.media || [];
                        const hasAudio = media.some((m: any) => m.type === 'audio');
                        const hasVoice = media.some((m: any) => m.type === 'voice') || p.type === 'voice';
                        return hasAudio && !hasVoice;
                    });
                    setMyAudioPosts(audio);
                })
                .catch(console.error)
                .finally(() => setIsLoadingPosts(false));
        }
    }, [isOwner, profileUser, isOpen]);

    if (!isOpen || !userData) return null;

    const handleSavePlaylist = async (updatedList: PlaylistItem[]) => {
        setPlaylist(updatedList);
        if (updateUserData && currentUser) {
            updateUserData((prev: any) => ({
                ...prev,
                [currentUser]: { ...prev[currentUser], playlist: updatedList }
            }));
            if (!isDemo) {
                try {
                    await updateUserOnServer(currentUser, { playlist: updatedList });
                } catch (e) {
                    console.error('Failed to save playlist to server', e);
                }
            }
        }
    };



    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const url = await uploadMediaFile(file, currentUser, (progress) => {
                setUploadProgress(Math.round(progress));
            });

            const newTrack: PlaylistItem = {
                id: Date.now().toString(),
                title: file.name.replace(/\.[^/.]+$/, ""), // Strip extension
                url: url
            };

            const updatedList = [...playlist, newTrack];
            await handleSavePlaylist(updatedList);
        } catch (err) {
            console.error('Failed to upload audio file', err);
            alert('Failed to upload audio file.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleAddFromPost = (post: any) => {
        const firstAudio = post.content?.media?.find((m: any) => m.type === 'audio');
        if (!firstAudio) return;

        const trackTitle = post.content?.text || firstAudio.name || `Audio Post #${post.id}`;

        const newTrack: PlaylistItem = {
            id: post.id.toString(),
            title: trackTitle,
            url: firstAudio.url,
            postId: post.id
        };

        // Avoid duplicates
        if (playlist.some(t => t.url === newTrack.url)) {
            alert('Track already in playlist.');
            return;
        }

        const updatedList = [...playlist, newTrack];
        handleSavePlaylist(updatedList);
    };

    const handleRemoveTrack = (index: number) => {
        const updatedList = playlist.filter((_, i) => i !== index);
        handleSavePlaylist(updatedList);
    };

    const handleMoveTrack = (index: number, direction: 'up' | 'down') => {
        const updatedList = [...playlist];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= updatedList.length) return;

        // Swap
        const temp = updatedList[index];
        updatedList[index] = updatedList[targetIndex];
        updatedList[targetIndex] = temp;

        handleSavePlaylist(updatedList);
    };

    return (
        <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-50 p-4 animate-fade-in"
            onClick={onClose}
        >
            <div 
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col border border-white/10 shadow-2xl backdrop-blur-md p-6 relative gap-4"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between w-full">
                    <span 
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                    >
                        {isEditing ? 'Manage Playlist' : `${userData.displayName}'s Playlist`}
                    </span>
                    <div className="flex items-center gap-2">
                        {isOwner && (
                            <button
                                onClick={() => setIsEditing(!isEditing)}
                                className="text-xs font-bold bg-black/20 hover:bg-black/40 px-3 py-1 rounded-full border border-white/10 transition-colors uppercase"
                                style={stickerTextStyle(0.18)}
                            >
                                {isEditing ? 'Done' : 'Edit'}
                            </button>
                        )}
                        <button 
                            onClick={onClose}
                            className="text-white/60 hover:text-white transition-colors"
                            title="Close"
                        >
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Playlist View */}
                {!isEditing ? (
                    <div className="flex flex-col gap-3 max-h-[360px] overflow-y-auto no-scrollbar">
                        {selectedExternalTrack && (() => {
                            const isSpotify = selectedExternalTrack.url.includes('spotify.com') || (selectedExternalTrack.originalUrl && selectedExternalTrack.originalUrl.includes('spotify.com'));
                            const isAppleMusic = selectedExternalTrack.url.includes('apple.com') || selectedExternalTrack.url.includes('music.apple.com') || (selectedExternalTrack.originalUrl && (selectedExternalTrack.originalUrl.includes('apple.com') || selectedExternalTrack.originalUrl.includes('music.apple.com')));
                            
                            const embedUrl = isSpotify 
                                ? getSpotifyEmbedUrl(selectedExternalTrack.originalUrl || selectedExternalTrack.url) 
                                : isAppleMusic 
                                    ? getAppleMusicEmbedUrl(selectedExternalTrack.originalUrl || selectedExternalTrack.url) 
                                    : null;
                            const cover = selectedExternalTrack.coverArt;
                            
                            return (
                                <div className="bg-black/30 border border-white/10 rounded-[1.5rem] p-4 flex flex-col gap-3 relative animate-fade-in mb-1">
                                    <div className="flex items-center justify-between">
                                        <span 
                                            className="text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-black/40 border border-white/5"
                                            style={{ color: isSpotify ? '#1DB954' : '#fc3c44', ...stickerTextStyle(0.18) }}
                                        >
                                            Now Playing {isSpotify ? 'Spotify' : 'Apple Music'} Track
                                        </span>
                                        <button 
                                            onClick={() => setSelectedExternalTrack(null)}
                                            className="text-[10px] font-black uppercase text-white/40 hover:text-white transition-colors"
                                            style={stickerTextStyle(0.18)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                    
                                    <div className="flex items-center gap-4 bg-black/20 border border-white/5 rounded-2xl p-3">
                                        {cover ? (
                                            <div className="relative w-16 h-16 flex-shrink-0 group">
                                                <img 
                                                    src={cover} 
                                                    alt="Cover art" 
                                                    className={`w-16 h-16 rounded-xl object-cover shadow-lg transition-transform duration-500 ${audioIsPlaying ? 'animate-[spin_10s_linear_infinite]' : ''}`} 
                                                />
                                                <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
                                                    <div className="w-4 h-4 bg-black rounded-full border-2 border-white/30" />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-16 h-16 flex-shrink-0 rounded-xl bg-gradient-to-br from-white/5 to-white/10 border border-white/5 flex items-center justify-center text-xl font-bold uppercase" style={{ color: userThemeColor }}>
                                                🎵
                                            </div>
                                        )}
                                        <div className="flex-grow min-w-0">
                                            <h4 className="font-bold text-sm truncate" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                                {selectedExternalTrack.title}
                                            </h4>
                                            <p className="text-[10px] text-white/50 uppercase tracking-wider mt-0.5" style={stickerTextStyle(0.18)}>
                                                {isSpotify ? 'Spotify' : 'Apple Music'} Premium Stream
                                            </p>
                                        </div>
                                    </div>

                                    {embedUrl && (
                                        <div className="w-full h-[80px] rounded-xl overflow-hidden mt-1 bg-black/10 border border-white/5">
                                            <iframe
                                                src={embedUrl}
                                                width="100%"
                                                height="80"
                                                frameBorder="0"
                                                allowFullScreen={false}
                                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                                loading="lazy"
                                                className="rounded-xl"
                                            />
                                        </div>
                                    )}

                                    {/* Sequential Flow Playlist Navigation Controls */}
                                    <div className="flex items-center justify-between px-1 mt-1 border-t border-white/5 pt-2">
                                        <button 
                                            onClick={handlePrevTrack}
                                            className="text-[10px] font-black uppercase text-white/50 hover:text-white flex items-center gap-1 transition-colors"
                                            style={stickerTextStyle(0.18)}
                                        >
                                            ◀ Prev Track
                                        </button>
                                        <button 
                                            onClick={handleNextTrack}
                                            className="text-[10px] font-black uppercase text-white/50 hover:text-white flex items-center gap-1 transition-colors"
                                            style={stickerTextStyle(0.18)}
                                        >
                                            Next Track ▶
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}

                        {playlist.length === 0 ? (
                            <div className="text-center py-8 text-white/50 text-xs uppercase tracking-wider font-bold">
                                No tracks in playlist yet.
                                {isOwner && <p className="mt-2 text-[10px] text-yellow-400">Click edit to add songs!</p>}
                            </div>
                        ) : (
                            playlist.map((track, index) => {
                                const isSpotify = track.url.includes('spotify.com');
                                const isApple = track.url.includes('apple.com') || track.url.includes('music.apple.com');
                                const isYoutube = track.url.includes('youtube.com') || track.url.includes('youtu.be');
                                const isSelected = selectedExternalTrack?.id === track.id;

                                return (
                                    <div 
                                        key={track.id}
                                        onClick={() => handlePlayTrack(track)}
                                        className={`flex items-center gap-3 bg-black/20 border rounded-2xl p-3 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] ${
                                            isSelected 
                                                ? 'border-yellow-400 shadow-md bg-black/40' 
                                                : 'border-white/10 hover:border-white/30'
                                        }`}
                                    >
                                        <span className="text-xs font-bold text-white/40">{index + 1}</span>
                                        <div className="flex-grow min-w-0">
                                            <p className="font-bold text-sm truncate" style={stickerTextStyle(0.18)}>
                                                {track.title}
                                            </p>
                                            {(isSpotify || isApple || isYoutube) && (
                                                <span 
                                                    className="inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full mt-0.5"
                                                    style={{ 
                                                        backgroundColor: isSpotify ? 'rgba(29, 185, 84, 0.15)' : isApple ? 'rgba(252, 60, 68, 0.15)' : 'rgba(255, 0, 0, 0.15)',
                                                        color: isSpotify ? '#1DB954' : isApple ? '#fc3c44' : '#ff0000',
                                                        border: `1px solid ${isSpotify ? 'rgba(29, 185, 84, 0.3)' : isApple ? 'rgba(252, 60, 68, 0.3)' : 'rgba(255, 0, 0, 0.3)'}`,
                                                        ...stickerTextStyle(0.18)
                                                    }}
                                                >
                                                    {isSpotify ? 'Spotify' : isApple ? 'Apple Music' : 'YouTube'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-yellow-400/20 text-yellow-400 text-xs">
                                            ▶
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                ) : (
                    /* Playlist Edit / Manage Mode */
                    <div className="flex flex-col gap-4">
                        {/* Current tracks and rearranging */}
                        <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto no-scrollbar bg-black/10 p-2 rounded-2xl border border-white/5">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-white/55 px-1">Tracks ({playlist.length})</p>
                            {playlist.length === 0 ? (
                                <p className="text-center text-[10px] text-white/40 uppercase py-4">Playlist is empty</p>
                            ) : (
                                playlist.map((track, index) => {
                                    const isEditingThis = editingTrackId === track.id;
                                    return (
                                        <div 
                                            key={track.id}
                                            draggable={!isEditingThis}
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            className="flex flex-col gap-2 bg-black/25 border border-white/5 rounded-2xl p-2.5 transition-all"
                                            style={{ opacity: index === draggedIndex ? 0.3 : 1 }}
                                        >
                                            {isEditingThis ? (
                                                <div className="flex flex-col gap-2 w-full">
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-[8px] font-bold uppercase tracking-wider text-white/50 pl-1">Track Name</label>
                                                        <input 
                                                            type="text"
                                                            value={editTitle}
                                                            onChange={(e) => setEditTitle(e.target.value)}
                                                            className="bg-black/30 border border-white/10 focus:border-white/30 rounded-xl px-2.5 py-1.5 text-xs font-bold text-white w-full outline-none"
                                                            placeholder="Edit Track Name"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-[8px] font-bold uppercase tracking-wider text-white/50 pl-1">Track Link</label>
                                                        <input 
                                                            type="text"
                                                            value={editLink}
                                                            onChange={(e) => setEditLink(e.target.value)}
                                                            className="bg-black/30 border border-white/10 focus:border-white/30 rounded-xl px-2.5 py-1.5 text-[10px] text-white/80 w-full outline-none"
                                                            placeholder="Edit URL (Audio file, Spotify, or Apple Music)"
                                                        />
                                                    </div>
                                                    <div className="flex items-center justify-end gap-1.5 mt-1">
                                                        <button 
                                                            onClick={() => setEditingTrackId(null)}
                                                            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white/50 hover:text-white transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button 
                                                            onClick={() => handleSaveEdit(index)}
                                                            disabled={isSavingEdit || !editTitle.trim() || !editLink.trim()}
                                                            className="px-3 py-1 bg-yellow-400 text-black rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-yellow-300 disabled:opacity-50 transition-all flex items-center gap-1"
                                                        >
                                                            {isSavingEdit ? 'Saving...' : 'Save'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between gap-2 w-full">
                                                    <div className="flex items-center gap-2 min-w-0 flex-grow">
                                                        <div 
                                                            className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/70 select-none w-5 h-5 flex items-center justify-center rounded hover:bg-white/5 text-xs font-bold"
                                                            title="Drag to rearrange"
                                                        >
                                                            ☰
                                                        </div>
                                                        <div className="flex flex-col min-w-0 flex-grow">
                                                            <span className="text-xs text-white/95 font-bold truncate">{track.title}</span>
                                                            {track.originalUrl && (
                                                                <span className="text-[8px] text-white/40 truncate mt-0.5">{track.originalUrl}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <button 
                                                            onClick={() => handleStartEdit(track)}
                                                            className="w-6 h-6 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded text-[10px]"
                                                            title="Edit Track"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button 
                                                            onClick={() => handleRemoveTrack(index)}
                                                            className="w-6 h-6 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded"
                                                        >
                                                            <TrashIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Add / Upload Section */}
                        <div className="flex flex-col gap-3 border-t border-white/10 pt-3">
                            {/* File Upload */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[9px] font-bold uppercase ml-1" style={stickerTextStyle(0.18)}>Upload audio track</label>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading}
                                    className="w-full bg-black/30 border border-white/10 hover:border-white/30 focus:border-white/30 rounded-2xl py-2 px-4 text-xs font-bold text-center transition-colors flex items-center justify-center gap-2"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    {isUploading ? `Uploading (${uploadProgress}%)` : 'Upload MP3 / Audio File'}
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    className="hidden"
                                />

                                {/* Streaming Service Toggle Buttons */}
                                <div className="grid grid-cols-3 gap-2 mt-1.5">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveInputService(activeInputService === 'spotify' ? null : 'spotify');
                                            setExternalError('');
                                        }}
                                        className="bg-black/30 border border-white/10 hover:border-white/30 rounded-2xl py-2 px-1 text-[10px] font-black uppercase text-center transition-colors flex items-center justify-center gap-1"
                                        style={{ 
                                            color: '#1DB954', 
                                            borderColor: activeInputService === 'spotify' ? '#1DB954' : 'rgba(255,255,255,0.1)',
                                            ...stickerTextStyle(0.18) 
                                        }}
                                    >
                                        🎵 Spotify
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveInputService(activeInputService === 'apple' ? null : 'apple');
                                            setExternalError('');
                                        }}
                                        className="bg-black/30 border border-white/10 hover:border-white/30 rounded-2xl py-2 px-1 text-[10px] font-black uppercase text-center transition-colors flex items-center justify-center gap-1"
                                        style={{ 
                                            color: '#fc3c44', 
                                            borderColor: activeInputService === 'apple' ? '#fc3c44' : 'rgba(255,255,255,0.1)',
                                            ...stickerTextStyle(0.18) 
                                        }}
                                    >
                                        🍎 Apple
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveInputService(activeInputService === 'youtube' ? null : 'youtube');
                                            setExternalError('');
                                        }}
                                        className="bg-black/30 border border-white/10 hover:border-white/30 rounded-2xl py-2 px-1 text-[10px] font-black uppercase text-center transition-colors flex items-center justify-center gap-1"
                                        style={{ 
                                            color: '#ff0000', 
                                            borderColor: activeInputService === 'youtube' ? '#ff0000' : 'rgba(255,255,255,0.1)',
                                            ...stickerTextStyle(0.18) 
                                        }}
                                    >
                                        📺 YouTube
                                    </button>
                                </div>

                                {/* Dynamic Streaming Addition Form */}
                                {activeInputService && (
                                    <form 
                                        onSubmit={handleAddStreamingTrack}
                                        className="bg-black/20 border border-white/5 rounded-2xl p-4 mt-2 flex flex-col gap-3 animate-fade-in"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span 
                                                className="text-[9px] font-black uppercase tracking-wider"
                                                style={{ 
                                                    color: activeInputService === 'spotify' ? '#1DB954' : activeInputService === 'apple' ? '#fc3c44' : '#ff0000', 
                                                    ...stickerTextStyle(0.18) 
                                                }}
                                            >
                                                Add {activeInputService === 'spotify' ? 'Spotify Track' : activeInputService === 'apple' ? 'Apple Music Track' : 'YouTube Music Track'}
                                            </span>
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    setActiveInputService(null);
                                                    setExternalError('');
                                                }}
                                                className="text-[8px] font-black uppercase text-white/40 hover:text-white"
                                            >
                                                Cancel
                                            </button>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="text-[8px] font-bold uppercase text-white/50 px-1">Song Link / URL</label>
                                            <input 
                                                type="text"
                                                placeholder={
                                                    activeInputService === 'spotify' 
                                                        ? "https://open.spotify.com/track/..." 
                                                        : activeInputService === 'apple' 
                                                            ? "https://music.apple.com/..." 
                                                            : "https://music.youtube.com/watch?v=..."
                                                }
                                                value={externalLink}
                                                onChange={(e) => setExternalLink(e.target.value)}
                                                className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 px-3 text-xs outline-none text-white font-mono"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="text-[8px] font-bold uppercase text-white/50 px-1">Song Title (Optional)</label>
                                            <input 
                                                type="text"
                                                placeholder="Enter song name and artist..."
                                                value={externalTitle}
                                                onChange={(e) => setExternalTitle(e.target.value)}
                                                className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 px-3 text-xs outline-none text-white"
                                            />
                                        </div>

                                        {externalError && (
                                            <p className="text-[9px] font-black uppercase text-red-400 px-1">{externalError}</p>
                                        )}

                                        <p className="text-[9px] text-white/30 px-1 leading-relaxed">
                                            not the song you're looking for? you can just post the link but it won't look as nice. sry :/
                                        </p>

                                        <button
                                            type="submit"
                                            className="w-full py-2 px-4 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all text-center cursor-pointer"
                                            style={{ 
                                                backgroundColor: activeInputService === 'spotify' ? '#1DB954' : '#fc3c44',
                                                ...stickerTextStyle(0.18)
                                            }}
                                        >
                                            Save to Playlist
                                        </button>
                                    </form>
                                )}
                            </div>

                            {/* Select from Audio Posts */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[9px] font-bold uppercase ml-1" style={stickerTextStyle(0.18)}>Add from your Audio Posts</label>
                                <div className="bg-black/20 border border-white/10 rounded-2xl p-2 max-h-[150px] overflow-y-auto no-scrollbar flex flex-col gap-1.5">
                                    {isLoadingPosts ? (
                                        <p className="text-center text-[10px] text-white/40 uppercase py-2">Loading posts...</p>
                                    ) : myAudioPosts.length === 0 ? (
                                        <p className="text-center text-[10px] text-white/45 uppercase py-2">No audio posts found</p>
                                    ) : (
                                        myAudioPosts.map((post) => (
                                            <div 
                                                key={post.id}
                                                className="flex items-center justify-between gap-2 p-1.5 hover:bg-white/5 rounded-lg text-xs"
                                            >
                                                <span className="truncate flex-grow text-white/80">
                                                    {post.content?.text || post.content?.media?.find((m: any) => m.type === 'audio')?.name || `Audio Post #${post.id}`}
                                                </span>
                                                <button
                                                    onClick={() => handleAddFromPost(post)}
                                                    className="px-2 py-1 bg-yellow-400/20 hover:bg-yellow-400/40 text-yellow-400 text-[10px] font-bold rounded-lg uppercase flex-shrink-0"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
